import type {
  CandidateProfile,
  ParsedResumeData,
  UpdateProfileBody,
} from '../../lib/apiClient';
import { normalizeUrl } from '../../lib/profileValidation';

/**
 * Editable form state for the candidate profile form (used by ProfileCompletionModal
 * Step 2 and MyProfilePage). Matches the PATCH `/candidates/me/profile` body shape
 * but keeps numeric inputs as strings for controlled `<input>` ergonomics.
 */
export interface WorkEntry {
  title: string;
  company: string;
  start_date: string;
  end_date: string;
  description: string;
}

export interface EducationEntry {
  degree: string;
  school: string;
  field: string;
  start_date: string;
  end_date: string;
}

export interface ProfileFormState {
  full_name: string;
  phone: string;
  linkedin_url: string;
  github_url: string;
  portfolio_url: string;
  current_role: string;
  current_company: string;
  /** Years (string for input). Converted to months on save. */
  years_experience: string;
  summary: string;
  experience_level_id: string;
  notice_period_id: string;
  expected_salary_min: string;
  expected_salary_max: string;
  expected_salary_currency_id: string;
  expected_salary_pay_period_id: string;
  willing_to_relocate: boolean;
  /** Right to Represent — radio: '' = unanswered, 'yes' / 'no'. Optional (No allowed). */
  rtr_consent: '' | 'yes' | 'no';
  /** Fraud declaration — radio: '' = unanswered, 'yes' / 'no'. Mandatory ('yes' required to save). */
  fraud_declaration: '' | 'yes' | 'no';
  soft_skills: string;
  tools: string;
  certifications: string;
  skill_ids: string[];
  work_history: WorkEntry[];
  education: EducationEntry[];
}

export function emptyForm(): ProfileFormState {
  return {
    full_name: '',
    phone: '',
    linkedin_url: '',
    github_url: '',
    portfolio_url: '',
    current_role: '',
    current_company: '',
    years_experience: '',
    summary: '',
    experience_level_id: '',
    notice_period_id: '',
    expected_salary_min: '',
    expected_salary_max: '',
    expected_salary_currency_id: '',
    expected_salary_pay_period_id: '',
    willing_to_relocate: false,
    rtr_consent: '',
    fraud_declaration: '',
    soft_skills: '',
    tools: '',
    certifications: '',
    skill_ids: [],
    work_history: [],
    education: [],
  };
}

function monthsToYears(months: number | null | undefined): string {
  if (months === null || months === undefined) return '';
  if (!Number.isFinite(months)) return '';
  return String(Math.round(months / 12));
}

function yearsToMonths(years: string): number | null {
  const trimmed = years.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 12);
}

function asString(v: string | null | undefined): string {
  return v ?? '';
}

function asArray(v: string[] | null | undefined): string[] {
  return v ?? [];
}

function joinList(v: string[] | null | undefined): string {
  return (v ?? []).join(', ');
}

function splitList(v: string): string[] {
  return v
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

export function formFromProfile(profile: CandidateProfile): ProfileFormState {
  const snapshot = profile.parsed_resume_snapshot;
  const work = (snapshot?.work_history ?? []).map((w) => ({
    title: asString(w?.title),
    company: asString(w?.company),
    start_date: asString(w?.start_date),
    end_date: asString(w?.end_date),
    description: asString(w?.description),
  }));
  const education = (snapshot?.education ?? []).map((e) => ({
    degree: asString(e?.degree),
    school: asString(e?.school),
    field: asString(e?.field),
    start_date: asString(e?.start_date),
    end_date: asString(e?.end_date),
  }));
  return {
    full_name: profile.full_name ?? '',
    phone: asString(profile.phone),
    linkedin_url: asString(profile.linkedin_url),
    github_url: asString(profile.github_url),
    portfolio_url: asString(profile.portfolio_url),
    current_role: asString(profile.current_role),
    current_company: asString(profile.current_company),
    years_experience: monthsToYears(profile.total_experience_months),
    summary: asString(profile.summary),
    experience_level_id: profile.experience_level?.id ?? '',
    notice_period_id: profile.notice_period?.id ?? '',
    expected_salary_min:
      profile.expected_salary_min === null ? '' : String(profile.expected_salary_min),
    expected_salary_max:
      profile.expected_salary_max === null ? '' : String(profile.expected_salary_max),
    expected_salary_currency_id: profile.expected_salary_currency?.id ?? '',
    expected_salary_pay_period_id: profile.expected_salary_pay_period?.id ?? '',
    willing_to_relocate: !!profile.willing_to_relocate,
    rtr_consent: profile.rtr_consent === true ? 'yes' : profile.rtr_consent === false ? 'no' : '',
    fraud_declaration: profile.fraud_declaration_consent_at ? 'yes' : '',
    soft_skills: joinList(profile.soft_skills),
    tools: joinList(profile.tools),
    certifications: joinList(profile.certifications),
    skill_ids: asArray(profile.skills?.map((s) => s.id)),
    work_history: work,
    education,
  };
}

export function formFromParsed(
  parsed: ParsedResumeData,
  matchedSkillIds: string[],
): ProfileFormState {
  const work = (parsed.work_history ?? []).map((w) => ({
    title: asString(w?.title),
    company: asString(w?.company),
    start_date: asString(w?.start_date),
    end_date: asString(w?.end_date),
    description: asString(w?.description),
  }));
  const education = (parsed.education ?? []).map((e) => ({
    degree: asString(e?.degree),
    school: asString(e?.school),
    field: asString(e?.field),
    start_date: asString(e?.start_date),
    end_date: asString(e?.end_date),
  }));
  return {
    full_name: asString(parsed.personal?.full_name),
    phone: asString(parsed.personal?.phone),
    // URLs from the AI parser often arrive as bare hosts (linkedin.com/in/foo) or
    // with leading whitespace; normalize so the field is immediately valid against
    // backend Zod `.url()` and the user doesn't see a generic "validation failed".
    linkedin_url: normalizeUrl(parsed.personal?.linkedin_url),
    github_url: normalizeUrl(parsed.personal?.github_url),
    portfolio_url: normalizeUrl(parsed.personal?.portfolio_url),
    current_role: asString(parsed.current_role),
    current_company: asString(parsed.current_company),
    years_experience: monthsToYears(parsed.total_experience_months),
    summary: asString(parsed.summary),
    experience_level_id: '',
    notice_period_id: '',
    expected_salary_min: '',
    expected_salary_max: '',
    expected_salary_currency_id: '',
    expected_salary_pay_period_id: '',
    willing_to_relocate: false,
    // Re-parse never carries declaration consents — the user attests separately
    // on save. The parent component preserves any prior values when merging.
    rtr_consent: '',
    fraud_declaration: '',
    soft_skills: joinList(parsed.soft_skills),
    tools: joinList(parsed.tools),
    certifications: joinList(parsed.certifications),
    skill_ids: matchedSkillIds,
    work_history: work,
    education,
  };
}

function nullableStr(v: string): string | null {
  const trimmed = v.trim();
  return trimmed === '' ? null : trimmed;
}

function nullableNum(v: string): number | null {
  const trimmed = v.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}

function nullableId(v: string): string | null {
  return v.trim() === '' ? null : v.trim();
}

export interface BuildPatchOptions {
  /** Audit hint when the request flips profile_completed=TRUE. */
  completion?: { gate?: 'soft' | 'hard'; via?: 'parsed' | 'manual' };
  /** Set true on first-completion saves so the snapshot is always sent (so the
   *  backend has work_history/education even when the candidate didn't edit). */
  forceSnapshot?: boolean;
}

export function buildUpdateBody(
  form: ProfileFormState,
  opts: BuildPatchOptions = {},
): UpdateProfileBody {
  const snapshot = {
    work_history: form.work_history.map((w) => ({
      title: nullableStr(w.title),
      company: nullableStr(w.company),
      start_date: nullableStr(w.start_date),
      end_date: nullableStr(w.end_date),
      description: nullableStr(w.description),
    })),
    education: form.education.map((e) => ({
      degree: nullableStr(e.degree),
      school: nullableStr(e.school),
      field: nullableStr(e.field),
      start_date: nullableStr(e.start_date),
      end_date: nullableStr(e.end_date),
    })),
  };
  const body: UpdateProfileBody = {
    full_name: form.full_name.trim(),
    phone: nullableStr(form.phone),
    // Belt-and-braces URL normalization at the wire boundary — covers the case
    // where the user pasted a bare host directly (without going through re-parse).
    linkedin_url: nullableStr(normalizeUrl(form.linkedin_url)),
    github_url: nullableStr(normalizeUrl(form.github_url)),
    portfolio_url: nullableStr(normalizeUrl(form.portfolio_url)),
    current_role: nullableStr(form.current_role),
    current_company: nullableStr(form.current_company),
    total_experience_months: yearsToMonths(form.years_experience),
    summary: nullableStr(form.summary),
    experience_level_id: nullableId(form.experience_level_id),
    notice_period_id: nullableId(form.notice_period_id),
    expected_salary_min: nullableNum(form.expected_salary_min),
    expected_salary_max: nullableNum(form.expected_salary_max),
    expected_salary_currency_id: nullableId(form.expected_salary_currency_id),
    expected_salary_pay_period_id: nullableId(form.expected_salary_pay_period_id),
    willing_to_relocate: form.willing_to_relocate,
    // Declaration consents — convert radio strings to wire format. RTR sends
    // boolean on yes/no, omitted when blank. Fraud only sends `true` (the only
    // value the backend accepts); 'no' / '' just don't include it and the
    // backend's mandatory-fraud guard will reject the save with a friendly error.
    ...(form.rtr_consent === 'yes' ? { rtr_consent: true as const } : {}),
    ...(form.rtr_consent === 'no' ? { rtr_consent: false as const } : {}),
    ...(form.fraud_declaration === 'yes' ? { fraud_declaration_consent: true as const } : {}),
    soft_skills: splitList(form.soft_skills),
    tools: splitList(form.tools),
    certifications: splitList(form.certifications),
    skill_ids: form.skill_ids,
    parsed_resume_snapshot:
      opts.forceSnapshot || snapshot.work_history.length || snapshot.education.length
        ? snapshot
        : undefined,
  };
  if (opts.completion) body.completion_context = opts.completion;
  return body;
}
